/* eslint-disable @typescript-eslint/no-explicit-any */
import { environment } from '@energy-stacks/feature-config';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material';

import { TourLabel } from './TourLabel';
import { TourInfo } from '../tour-details/TourDetails';
import { SIDE_LIST_WIDTH, ToursMapSideList } from './ToursMapSideList';
import { vehicleColors } from '../vehicleColors';
import { decode } from '@mapbox/polyline';
import { compact } from 'lodash-es';
import {
  Map as AppMap,
  Coords,
  FleetProduct,
  MapProps,
  MapWrapper,
  OffsetPoint,
  PlantMarker,
  mapDefaultProps as defaultProps,
} from '@energy-stacks/fleet/shared';
import { JOB_MAP_POPUP_WIDTH, JobMapPopupCard } from './JobMapPopupCard';
import { usePrevious } from '@energy-stacks/core/ui';
import { useSelectedPlant } from '@energy-stacks/fleet/feature-business-accounts';

import './tourDetailsMap.css';
import { tourDetailsMapControlIcon } from './tourDetailsMapControlIcon';
import { useTranslation } from 'react-i18next';
import { useJobsMapMarkers } from './useJobsMapMarkers';
import { TourJobMarker } from './markers/TourJobMarker';
import { PoolJobMarker } from './markers/PoolJobMarker';
import { PoolJobsCluster } from './markers/PoolJobsCluster';
import { MapLegend } from './MapLegend';
import { EditingTourJob } from '../edit-tour/editingTourJobModel';

export type PointJob = OffsetPoint<{
  tourIndex: number;
  visitOrder: number;
  jobId: string;
  jobProduct: FleetProduct;
}>;

export type MapBounds = [number, number, number, number] | undefined;

const getBounds = (polyline: google.maps.Polyline) => {
  const bounds = new google.maps.LatLngBounds();
  polyline.getPath().forEach((item, _index) => {
    bounds.extend(new google.maps.LatLng(item.lat(), item.lng()));
  });
  return bounds;
};

const LEAVES_POINT_LIMIT = 30;

type TourDetailsMapProps = {
  tours: TourInfo[];
  onAddToTour?: (job: EditingTourJob) => void;
  jobPool?: EditingTourJob[];
  mapProps?: MapProps;
  highlightedJobVisitOrder?: number | null;
};

export const TourDetailsMap: React.FC<TourDetailsMapProps> = ({
  tours,
  mapProps,
  jobPool = [],
  onAddToTour,
  highlightedJobVisitOrder,
}) => {
  const mapRef = useRef(null);
  const [t] = useTranslation('tours');
  const theme = useTheme();
  const [hoveredRouteInfo, setRouteInfo] = useState<{
    lat: number;
    lng: number;
    name: string;
    tourIndex: number;
  }>();

  const selectedPlant = useSelectedPlant();

  const [focusedTour, setFocusedTour] = useState<(typeof tours)[0] | undefined>(
    tours.length === 1 ? tours[0] : undefined
  );

  const polylinesRef = useRef<
    {
      name: string;
      strokeColor: string;
      polyline: google.maps.Polyline;
      zIndex: number;
    }[]
  >([]);

  const [zoom, setZoom] = useState(defaultProps.zoom);
  const [isMouseOverPopup, setIsMouseOverPopup] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<
    | {
        coords: [string, string];
        visitOrder: number;
        tourIndex: number;
        clusterId?: number;
        jobId: string;
      }[]
    | undefined
  >(undefined);
  const [mapReady, setMapReady] = useState(false);
  const [bounds, setBounds] = useState<MapBounds>();
  const handleGoogleApiLoaded = ({ map }: any) => {
    mapRef.current = map;
    setMapReady(true);
  };
  const previousZoom = usePrevious(zoom);

  const focusedTourJobs = useMemo(() => {
    return tours
      .filter((tour) =>
        !focusedTour ? true : tour.tourIndex === focusedTour?.tourIndex
      )
      .flatMap((tour) =>
        tour.tourJobs.jobs
          .filter((job) => job.origin && job.destination)
          .map((job) => ({ ...job, tourIndex: tour.tourIndex }))
      );
  }, [focusedTour, tours]);

  // Tour jobs
  const {
    clusters: tourJobsClusters,
    supercluster: tourJobsSuperCluster,
    jobPoints: tourJobsPoints,
  } = useJobsMapMarkers(focusedTourJobs, bounds, zoom, 'tour_jobs');

  // Job pool
  const {
    clusters: jobPoolClusters,
    supercluster: jobPoolSuperCluster,
    jobPoints: jobPoolPoints,
  } = useJobsMapMarkers(jobPool, bounds, zoom, 'job_pool');

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const darkMainColor = theme.palette.dark.main;

  useEffect(() => {
    if (!mapRef.current || !mapReady || !window.google) return;

    const listeners: google.maps.MapsEventListener[] = [];

    const polylines = tours
      .filter((tour) => tour.polyline !== '')
      .map(({ polyline, vehicleName: name, tourIndex }) => {
        const stroke = vehicleColors[tourIndex % vehicleColors.length];
        const path = decode(polyline).map((point) => ({
          lat: point[0],
          lng: point[1],
        }));

        const tourPath = new google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor: 'white',
          strokeOpacity: 1.0,
          zIndex: 2,
          strokeWeight: 1,
        });

        const tourPathDouble = new google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor: stroke,
          zIndex: 1,
          strokeOpacity: 1.0,
          strokeWeight: 5,
        });

        const mouseOverListener = google.maps.event.addListener(
          tourPath,
          'mouseover',
          function (e: any) {
            setRouteInfo({
              lat: e.latLng.lat(),
              lng: e.latLng.lng(),
              name,
              tourIndex,
            });
          }
        );

        const mouseOverListenerDouble = google.maps.event.addListener(
          tourPathDouble,
          'mouseover',
          function (e: any) {
            setRouteInfo({
              lat: e.latLng.lat(),
              lng: e.latLng.lng(),
              name,
              tourIndex,
            });
          }
        );

        const mouseOutListener = google.maps.event.addListener(
          tourPath,
          'mouseout',
          function () {
            setRouteInfo(undefined);
          }
        );

        const mouseOutListenerDouble = google.maps.event.addListener(
          tourPathDouble,
          'mouseout',
          function () {
            setRouteInfo(undefined);
          }
        );

        listeners.push(
          mouseOverListener,
          mouseOutListener,
          mouseOverListenerDouble,
          mouseOutListenerDouble
        );

        return [
          { stroke, name: tourIndex.toString(), path: tourPath, zIndex: 2 },
          {
            stroke,
            name: tourIndex.toString(),
            path: tourPathDouble,
            zIndex: 1,
          },
        ];
      })
      .flat();

    polylines.forEach(({ name, path: polyline, zIndex, stroke }) => {
      polyline.setMap(mapRef.current);
      polylinesRef.current.push({
        name,
        polyline,
        zIndex,
        strokeColor: stroke,
      });
    });

    return () => {
      polylines.forEach(({ path: polyline }) => {
        polyline.setMap(null);
        polylinesRef.current = [];
      });
      listeners.forEach((listener) => {
        google.maps.event.removeListener(listener);
      });
    };
  }, [mapRef, mapReady, tours, darkMainColor]);

  useEffect(() => {
    if (!mapRef.current || !mapReady || !window.google || !focusedTour) return;

    // Fit polyline bounds
    const focusedPolyline = polylinesRef.current.find(
      ({ name }) => name === focusedTour.tourIndex.toString()
    )?.polyline;

    if (focusedPolyline) {
      const focusedBounds = getBounds(focusedPolyline);

      // @ts-expect-error No types
      mapRef.current.fitBounds(focusedBounds, {
        left: tours.length > 1 ? SIDE_LIST_WIDTH : 0,
        right: 0,
        bottom: 0,
        top: 0,
      });

      polylinesRef.current.forEach(({ polyline, name }) => {
        if (name === focusedTour.tourIndex.toString()) return;
        polyline.setOptions({
          strokeOpacity: 0.2,
        });
      });
    }

    return () => {
      polylinesRef.current.forEach(({ polyline }) => {
        polyline.setOptions({
          strokeOpacity: 1.0,
        });
      });
    };
  }, [tours, focusedTour, mapReady]);

  useEffect(() => {
    if (!mapRef.current || !mapReady || !window.google || !focusedTour) return;

    polylinesRef.current.forEach((drawing) => {
      const { polyline, name, zIndex } = drawing;
      if (name === focusedTour.tourIndex.toString()) {
        drawing.zIndex = zIndex + 2;
        polyline.setOptions({
          zIndex: drawing.zIndex,
        });
      }
    });

    return () => {
      polylinesRef.current.forEach((drawing) => {
        const { polyline, zIndex } = drawing;
        if (zIndex === 3) {
          drawing.zIndex = 1;
          polyline.setOptions({
            zIndex: drawing.zIndex,
          });
        } else if (zIndex === 4) {
          drawing.zIndex = 2;
          polyline.setOptions({
            zIndex: drawing.zIndex,
          });
        }
      });
    };
  }, [focusedTour, mapReady]);

  const fitToursToMapBounds = useCallback(() => {
    const toursBounds = compact(
      tours.map((tour) => {
        const polyline = polylinesRef.current.find(
          (polyline) => polyline.name === tour.tourIndex.toString()
        )?.polyline;
        return polyline && getBounds(polyline);
      })
    );

    const mapBounds = toursBounds.reduce(
      (bounds, tour) =>
        bounds.extend(tour.getNorthEast()).extend(tour.getSouthWest()),
      new google.maps.LatLngBounds()
    );
    //@ts-expect-error No types
    mapRef.current.fitBounds(mapBounds, {
      left: SIDE_LIST_WIDTH,
      right: 0,
      bottom: 0,
      top: 0,
    });
  }, [tours]);

  useEffect(() => {
    // We need to wait on map ready here since we need to check what polylines are currently visible on the map
    if (!mapRef.current || !mapReady || !window.google) return;

    // No need to fit bounds if there is only one tour since it will be focused and fitted to the map by default
    if (tours.length > 1) {
      // There should always be a polyline for each tour but compact just in case
      fitToursToMapBounds();
    }
  }, [fitToursToMapBounds, mapReady, tours]);

  const handleDeselectJobs = useCallback(() => {
    setSelectedJobs(undefined);
  }, []);

  useEffect(() => {
    if (
      !mapRef.current ||
      !mapReady ||
      !window.google ||
      !selectedJobs ||
      !previousZoom
    )
      return;

    if (zoom !== previousZoom) {
      handleDeselectJobs();
    }
  }, [selectedJobs, mapReady, zoom, previousZoom, handleDeselectJobs]);

  const showOnMap = useCallback(
    (clusterId: number, lat: number, lng: number, superCluster: any) => {
      const expansionZoom = Math.min(
        superCluster.getClusterExpansionZoom(clusterId),
        20
      );
      //@ts-expect-error No types
      mapRef.current?.setZoom(expansionZoom + 1);
      //@ts-expect-error No types
      mapRef.current?.panTo({ lat, lng });
    },
    []
  );

  const selectedJobsInfo = useMemo(() => {
    if (!selectedJobs) {
      return [];
    }

    const filteredTours = tours.filter((tour) =>
      selectedJobs.find((job) => job.tourIndex === tour.tourIndex)
    );

    const tourJobs = filteredTours
      .map((tour) =>
        tour.tourJobs.jobs.filter((tourJobs) =>
          selectedJobs.find((job) => job.jobId === tourJobs.jobId)
        )
      )
      .flat();

    const poolJobs = jobPool.filter((job) =>
      selectedJobs.find((selectedJob) => job.jobId === selectedJob.jobId)
    );

    return [
      ...tourJobs.map((job) => ({
        ...job,
        clusterId: selectedJobs[0].clusterId,
        isFromTour: true,
      })),
      ...poolJobs.map((job) => ({
        ...job,
        clusterId: selectedJobs[0].clusterId,
        isFromTour: false,
      })),
    ];
  }, [jobPool, selectedJobs, tours]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    if (selectedJobsInfo) {
      //@ts-expect-error No types
      mapRef.current.setOptions({
        disableDoubleClickZoom: true,
      });
    } else {
      //@ts-expect-error No types
      mapRef.current.setOptions({
        disableDoubleClickZoom: false,
      });
    }
  }, [selectedJobsInfo, mapReady]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;

    const controlDiv = document.createElement('div');
    const controlButton = document.createElement('button');
    controlButton.classList.add('map-control-zoom-out-button');

    controlButton.innerHTML = tourDetailsMapControlIcon;
    controlButton.title = t('mapZoomOutButtonTooltip');
    controlButton.type = 'button';

    controlButton.addEventListener('click', () => {
      fitToursToMapBounds();
    });

    controlDiv.appendChild(controlButton);

    //@ts-expect-error No types
    mapRef.current.controls[
      google.maps.ControlPosition.INLINE_END_BLOCK_END
    ].push(controlDiv);

    return () => {
      //@ts-expect-error No types
      mapRef?.current?.controls[
        google.maps.ControlPosition.INLINE_END_BLOCK_END
      ].pop();
    };
  }, [mapReady, t, fitToursToMapBounds]);

  const handleMarkerClick = useCallback(
    (
      isCluster: boolean,
      clusterId: number,
      coords: Coords,
      jobPoints: PointJob[],
      superCluster: any
    ) => {
      if (!mapReady) {
        return;
      }

      // @ts-expect-error No types
      mapRef.current.panTo({
        lat: coords.lat,
        lng: coords.lng,
      });
      // @ts-expect-error No types
      mapRef.current.panBy(JOB_MAP_POPUP_WIDTH / 2, 339.5 / 2); // Make popup appear at the center of the map

      if (isCluster) {
        const clusterProperties = superCluster
          .getLeaves(clusterId, LEAVES_POINT_LIMIT)
          .map((point: any) => ({
            details: jobPoints.find(
              (p) =>
                point.geometry.coordinates[0] === p.coords[1] &&
                point.geometry.coordinates[1] === p.coords[0]
            ),
            clusterId: clusterId,
          }));

        if (clusterProperties.length === LEAVES_POINT_LIMIT) {
          return showOnMap(clusterId, coords.lat, coords.lng, superCluster);
        }

        setSelectedJobs(
          clusterProperties.map((props: any) => ({
            visitOrder: props.details?.properties.visitOrder,
            jobId: props.details?.properties.jobId,
            tourIndex: props.details?.properties.tourIndex,
            coords: props.details?.coords,
            clusterId: clusterId,
          }))
        );
      } else {
        const jobPoint = jobPoints.find(
          (point) =>
            parseFloat(point.coords[0]) === coords.lat &&
            parseFloat(point.coords[1]) === coords.lng
        );
        setSelectedJobs(
          jobPoint
            ? [
                {
                  ...jobPoint.properties,
                  coords: jobPoint.coords,
                },
              ]
            : undefined
        );
      }
    },
    [mapReady, showOnMap]
  );

  const handleJobMapPopupCardMouseLeave = useCallback(() => {
    setIsMouseOverPopup(false);
  }, []);

  const handleJobMapPopupCardMouseEnter = useCallback(() => {
    if (isMouseOverPopup) {
      return;
    }
    setIsMouseOverPopup(true);
  }, [isMouseOverPopup]);

  const handleAddJobToTour = useCallback(
    (job: EditingTourJob) => onAddToTour?.(job),
    [onAddToTour]
  );

  const handleShowJobOnMap = useCallback(
    (clusterId: number) => {
      if (!selectedJobs) {
        return;
      }
      const isFromTour = selectedJobsInfo.find(
        (job) => clusterId === job.clusterId
      )?.isFromTour;

      showOnMap(
        clusterId,
        parseFloat(selectedJobs[0].coords[0]),
        parseFloat(selectedJobs[0].coords[1]),
        isFromTour ? tourJobsSuperCluster : jobPoolSuperCluster
      );
      handleDeselectJobs();
    },
    [
      handleDeselectJobs,
      jobPoolSuperCluster,
      selectedJobs,
      selectedJobsInfo,
      showOnMap,
      tourJobsSuperCluster,
    ]
  );

  return (
    <MapWrapper>
      {tours.length > 1 && (
        <ToursMapSideList
          tours={tours}
          focusedTour={focusedTour}
          onTourFocus={(tour) => {
            if (focusedTour && focusedTour.tourIndex === tour.tourIndex) {
              setFocusedTour(undefined);
            } else {
              setFocusedTour(tour);
            }
          }}
        />
      )}
      <AppMap
        onChange={({ zoom, bounds }: { zoom: number; bounds: any }) => {
          setZoom(zoom);
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          setBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
        }}
        apiKey={environment.googleApiKey}
        onMapLoaded={handleGoogleApiLoaded}
        defaultCenter={{
          lat:
            parseFloat(selectedPlant?.geoLocation.latitude ?? '') ||
            defaultProps.center.lat,
          lng:
            parseFloat(selectedPlant?.geoLocation.longitude ?? '') ||
            defaultProps.center.lng,
        }}
        resetBoundsOnResize
        defaultZoom={defaultProps.zoom}
        {...mapProps}
      >
        {jobPoolClusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster } = cluster.properties;

          const coords = {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          };

          if (!isCluster) {
            const jobPoint = jobPoolPoints.find(
              (point) =>
                point.coords[0] === latitude && point.coords[1] === longitude
            );

            return (
              <PoolJobMarker
                key={`${cluster.id}-${parseFloat(latitude)}-${parseFloat(
                  longitude
                )}`}
                lat={parseFloat(latitude)}
                lng={parseFloat(longitude)}
                productType={jobPoint?.properties.jobProduct?.name ?? ''} // Should never be -1
                onClick={() => {
                  handleMarkerClick(
                    false,
                    cluster.id,
                    coords,
                    jobPoolPoints,
                    jobPoolSuperCluster
                  );
                }}
              />
            );
          }

          const leaves = jobPoolSuperCluster.getLeaves(cluster.id, Infinity);

          const productTypes = leaves.map(
            (point: any) => point.properties.jobProduct?.name ?? ''
          ) as string[];

          return (
            <PoolJobsCluster
              key={cluster.id}
              lat={parseFloat(latitude)}
              lng={parseFloat(longitude)}
              productTypes={productTypes}
              onClick={() => {
                handleMarkerClick(
                  true,
                  cluster.id,
                  coords,
                  jobPoolPoints,
                  jobPoolSuperCluster
                );
              }}
            />
          );
        })}
        {tourJobsClusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster } = cluster.properties;
          const coords = {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          };

          let visitOrders;
          let jobPoint: PointJob | undefined;
          if (!isCluster) {
            jobPoint = tourJobsPoints.find(
              (point) =>
                point.coords[0] === latitude && point.coords[1] === longitude
            );
            visitOrders = [
              jobPoint?.properties.visitOrder ?? -1, // Should never be -1
            ];
          } else {
            const leaves = tourJobsSuperCluster.getLeaves(
              cluster.id,
              LEAVES_POINT_LIMIT
            );

            visitOrders = leaves
              .map((point: any) => point.properties.jobVisitOrder)
              .sort((a: number, b: number) => a - b) as number[];
          }

          return (
            <TourJobMarker
              isHighlighted={Boolean(
                visitOrders.find(
                  (order) => order - 1 === highlightedJobVisitOrder
                )
              )}
              key={cluster.id}
              lat={coords.lat}
              lng={coords.lng}
              jobVisitOrders={visitOrders}
              onClick={() => {
                handleMarkerClick(
                  isCluster,
                  cluster.id,
                  coords,
                  tourJobsPoints,
                  tourJobsSuperCluster
                );
              }}
            />
          );
        })}
        {selectedPlant?.geoLocation && (
          <PlantMarker
            onClick={() => {
              if (!mapReady) return;
              //@ts-expect-error No types
              mapRef.current?.setZoom(defaultProps.maxZoom - 2);
              //@ts-expect-error No types
              mapRef.current?.panTo({
                lat: parseFloat(selectedPlant.geoLocation.latitude),
                lng: parseFloat(selectedPlant.geoLocation.longitude),
              });
            }}
            lat={parseFloat(selectedPlant.geoLocation.latitude)}
            lng={parseFloat(selectedPlant.geoLocation.longitude)}
          />
        )}
        {hoveredRouteInfo && !isMouseOverPopup && (
          <TourLabel
            style={{ zIndex: 100 }}
            title={hoveredRouteInfo.name}
            lat={hoveredRouteInfo.lat}
            lng={hoveredRouteInfo.lng}
            colorIndex={hoveredRouteInfo.tourIndex}
          />
        )}
        {selectedJobs && selectedJobsInfo && selectedJobsInfo.length > 0 && (
          <JobMapPopupCard
            zIndex={1000}
            jobPool={jobPool}
            onAddToTour={handleAddJobToTour}
            onClose={handleDeselectJobs}
            onMouseOver={handleJobMapPopupCardMouseEnter}
            onMouseLeave={handleJobMapPopupCardMouseLeave}
            onShowOnMap={handleShowJobOnMap}
            lat={parseFloat(selectedJobs[0].coords[0]) - 0.00001}
            lng={parseFloat(selectedJobs[0].coords[1])}
            jobs={selectedJobsInfo}
            clusterId={selectedJobs[0].clusterId}
          />
        )}
      </AppMap>
      <MapLegend />
    </MapWrapper>
  );
};
